import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Spinner } from '@heroui/react'
import confetti from 'canvas-confetti'
import { useAppStore } from '@/store'

import SuccessIcon from '@/components/icons/SuccessIcon'

export default function StepsDone() {
	const { user } = useAppStore()
	const nav = useNavigate()
	const [isLoading] = useState(false)
	const status = user?.application_process

	// Only run confetti for Submitted or Admitted statuses
	useEffect(() => {
		if (!user) return
		if (status !== 'Submitted' && status !== 'Admitted') return

		const stop = runConfetti()
		return () => stop()
	}, [user, status])

	if (isLoading) {
		return (
			<div className="flex min-h-[40vh] items-center justify-center">
				<Spinner />
			</div>
		)
	}

	// Not Accepted status
	if (status === 'Not Accepted') {
		return (
			<div className="flex min-h-[calc(100vh-20rem)] flex-1 items-center justify-center">
				<div className="mx-auto max-w-2xl space-y-8 px-4 text-center">
					<h2 className="mb-2 text-3xl font-semibold">
						Thank you{user?.name ? `, ${user.name}` : ''}
					</h2>
					<p className="text-default-500">
						Thank you for your interest in Leangap. Unfortunately, we were not able to offer you a
						spot in this year's program.
					</p>
					<p>
						We encourage you to apply again next year. We wish you all the best in your future
						endeavors.
					</p>
				</div>
			</div>
		)
	}

	// Admitted status
	if (status === 'Admitted') {
		return (
			<div className="flex min-h-[calc(100vh-20rem)] flex-1 items-center justify-center">
				<div id="confetti" className="pointer-events-none fixed inset-0 z-50" />
				<div className="mx-auto max-w-2xl space-y-8 px-4 text-center">
					<div className="mb-8 flex justify-center">
						<SuccessIcon width={100} height={100} />
					</div>
					<h2 className="mb-2 text-3xl font-semibold">
						Congratulations{user?.name ? `, ${user.name}` : ''}!
					</h2>
					<p className="text-default-500">
						You have been admitted to Leangap! We're thrilled to have you join us.
					</p>
					<p>
						Check your email to find your admission letter. You can complete your enrollment and
						secure your spot in the program by paying your tuition. See your available program
						options below:
					</p>
					{/* In-app route: a full-page navigation here would abort the ripple's lazy chunk import on WebKit. */}
					<Button color="primary" size="lg" className="mt-4" onPress={() => nav('/tuition')}>
						See Available Programs
					</Button>
				</div>
			</div>
		)
	}

	// Default: Submitted status
	return (
		<div className="flex min-h-[calc(100vh-20rem)] flex-1 items-center justify-center">
			<div id="confetti" className="pointer-events-none fixed inset-0 z-50" />
			<div className="mx-auto max-w-2xl space-y-8 px-4 text-center">
				<div className="mb-8 flex justify-center">
					<SuccessIcon width={100} height={100} />
				</div>
				<h2 className="mb-2 text-3xl font-semibold">
					Congratulations{user?.name ? `, ${user.name}` : ''}!
				</h2>
				<p className="text-default-500">
					Your application
					{user?.email ? (
						<>
							{' with the email '}
							<span className="text-white">{user.email}</span>
						</>
					) : null}
					{' has been successfully submitted.'}
				</p>
				<p className="">
					You will receive a status update via email within 10 to 20 days. Please regularly check
					your <span className="font-semibold">Promotions</span>,{' '}
					<span className="font-semibold">Social</span>, and{' '}
					<span className="font-semibold">Spam</span> folders in case our emails land there.
				</p>
				<p>
					You <span className="font-semibold">do not need</span> to take any further action or sign
					back into your application. We will stay in touch by email only.
				</p>
			</div>
		</div>
	)
}

function randomInRange(min: number, max: number) {
	return Math.random() * (max - min) + min
}

function runConfetti() {
	const duration = 5 * 1000
	const animationEnd = Date.now() + duration
	const defaults = {
		startVelocity: 15,
		spread: 360,
		ticks: 60,
		zIndex: 1000,
		resize: true,
		useWorker: true,
		scalar: 1,
		disableForReducedMotion: true,
		origin: { y: 0.6 },
	}

	// Use window timers in the browser environment
	const interval = window.setInterval(() => {
		const timeLeft = animationEnd - Date.now()
		if (timeLeft <= 0) {
			window.clearInterval(interval)
			return
		}
		const particleCount = 50 * (timeLeft / duration)
		confetti({
			...defaults,
			particleCount,
			origin: { x: randomInRange(0.2, 0.8), y: Math.random() * 0.4 + 0.1 },
		})
	}, 250)

	// return stopper so caller can clear interval early (e.g., on logout)
	return () => {
		window.clearInterval(interval)
		// optionally remove confetti canvas nodes created by canvas-confetti
		try {
			const nodes = Array.from(document.querySelectorAll('canvas[aria-hidden]'))
			nodes.forEach((n) => n.remove())
		} catch {
			// ignore
		}
	}
}
